import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { downloadPDFGuide } from '../utils/downloadUtils';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Mock service data based on Figma wireframe
  const service = {
    id: serviceId || '1',
    name: 'AI Restaurant Automation',
    description: 'Streamlining front-of-house and back-of-house operations via intelligent agents and autonomous workflows. Increase table turnover by 20% and reduce food waste by 15%.',
    category: 'Enterprise Solution',
    status: 'Active',
    pricing: '$2,999/month',
    agents: [
      {
        name: 'Order Processing Agent',
        description: 'Handles multi-channel orders (App, Web, Kiosk) and routes them to optimal prep stations.',
        icon: 'shopping_cart_checkout',
        status: 'Active'
      },
      {
        name: 'Stock Predictive Agent', 
        description: 'Auto-generates purchase orders based on shelf-life and historical consumption patterns.',
        icon: 'inventory_2',
        status: 'Active'
      },
      {
        name: 'Customer Sentiment Bot',
        description: 'Monitors social reviews and in-app feedback to flag service issues in real-time.',
        icon: 'reviews',
        status: 'Beta'
      }
    ],
    problemMapping: [
      {
        problem: 'Staffing Gaps & Turnover',
        problemDesc: 'High churn rates lead to inconsistent customer service and missed booking calls.',
        solution: 'AI Voice Concierge',
        solutionDesc: '24/7 autonomous phone handling for bookings, FAQs, and specialized requests.'
      },
      {
        problem: 'Inaccurate Stock Forecasting',
        problemDesc: 'Over-ordering leads to waste, under-ordering leads to 86\'d menu items.',
        solution: 'Predictive Inventory Bot',
        solutionDesc: 'Real-time demand forecasting synced with POS data and local events.'
      }
    ]
  };

  const [reviews] = useState([
    {
      id: 1,
      user: 'Sarah Chen',
      rating: 5,
      comment: 'Excellent service! Reduced our support ticket response time by 70%.',
      date: '2024-01-28',
      avatar: 'SC'
    },
    {
      id: 2,
      user: 'Marcus Johnson',
      rating: 4,
      comment: 'Great automation capabilities. Setup was straightforward.',
      date: '2024-01-25',
      avatar: 'MJ'
    },
    {
      id: 3,
      user: 'Elena Rodriguez',
      rating: 5,
      comment: 'Perfect for our customer service needs. Highly recommended!',
      date: '2024-01-22',
      avatar: 'ER'
    }
  ]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span 
        key={i} 
        className={`material-symbols-outlined text-sm ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      >
        star
      </span>
    ));
  };

  return (
    <div style={{ backgroundColor: '#101c22', color: 'white', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header - Exact match to Figma */}
      <header className="flex items-center justify-between whitespace-nowrap border-b border-[#233c48] px-10 py-3 sticky top-0 z-50" style={{ backgroundColor: '#101c22' }}>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-4 text-white">
            <div className="w-6 h-6 text-[#13a4ec]">
              <span className="material-symbols-outlined text-3xl">deployed_code</span>
            </div>
            <h2 className="text-white text-lg font-bold leading-tight tracking-[-0.015em]">OrbitMind</h2>
          </div>
          <nav className="flex items-center gap-9">
            <Link to="/solutions" className="text-[#92b7c9] hover:text-white text-sm font-medium leading-normal transition-colors">Solutions</Link>
            <Link to="/platform" className="text-[#92b7c9] hover:text-white text-sm font-medium leading-normal transition-colors">Platform</Link>
            <Link to="/resources" className="text-[#92b7c9] hover:text-white text-sm font-medium leading-normal transition-colors">Resources</Link>
            <Link to="/pricing" className="text-white text-sm font-medium leading-normal">Pricing</Link>
          </nav>
        </div>
        <div className="flex flex-1 justify-end gap-8">
          <label className="flex flex-col min-w-40 h-10 max-w-64">
            <div className="flex w-full flex-1 items-stretch rounded-lg h-full">
              <div className="text-[#92b7c9] flex border-none bg-[#233c48] items-center justify-center pl-4 rounded-l-lg border-r-0">
                <span className="material-symbols-outlined text-lg">search</span>
              </div>
              <input 
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-white focus:outline-0 focus:ring-0 border-none bg-[#233c48] focus:border-none h-full placeholder:text-[#92b7c9] px-4 rounded-l-none border-l-0 pl-2 text-base font-normal leading-normal" 
                placeholder="Search Docs..." 
              />
            </div>
          </label>
          <div className="flex gap-2">
            <button className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-[#13a4ec] text-white text-sm font-bold leading-normal tracking-[0.015em] hover:bg-[#13a4ec]/80 transition-all">
              <span className="truncate">Contact Sales</span>
            </button>
            <button className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-[#233c48] text-white text-sm font-bold leading-normal tracking-[0.015em] hover:bg-[#2d4d5c] transition-all">
              <span className="truncate">Login</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-10 xl:px-40 py-8 flex flex-col gap-8 max-w-[1440px] mx-auto w-full">
        {/* Breadcrumbs - Exact match to Figma */}
        <div className="flex flex-wrap gap-2 py-2">
          <Link to="/" className="text-[#92b7c9] hover:text-white text-sm font-medium transition-colors">Home</Link>
          <span className="text-[#92b7c9] text-sm">/</span>
          <Link to="/services" className="text-[#92b7c9] hover:text-white text-sm font-medium transition-colors">Services</Link>
          <span className="text-[#92b7c9] text-sm">/</span>
          <span className="text-white text-sm font-medium">{service.name}</span>
        </div>

        {/* Page Heading - Exact match to Figma */}
        <div className="flex flex-wrap justify-between items-end gap-6 bg-[#182a33] p-8 rounded-xl border border-[#233c48]">
          <div className="flex min-w-72 flex-col gap-4 max-w-2xl">
            <div className="inline-flex px-3 py-1 rounded-full bg-[#13a4ec]/10 border border-[#13a4ec]/20 w-fit">
              <span className="text-[#13a4ec] text-xs font-bold uppercase tracking-wider">{service.category}</span>
            </div>
            <h1 className="text-white text-5xl font-black leading-tight tracking-[-0.033em]">{service.name}</h1>
            <p className="text-[#92b7c9] text-lg font-normal leading-relaxed">
              {service.description}
            </p>
            {/* Chips */}
            <div className="flex gap-3 flex-wrap pt-2">
              <div className="flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-[#233c48] px-4 border border-[#305263]">
                <span className="material-symbols-outlined text-sm text-[#13a4ec]">schedule</span>
                <p className="text-white text-xs font-medium">24/7 Ops</p>
              </div>
              <div className="flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-[#233c48] px-4 border border-[#305263]">
                <span className="material-symbols-outlined text-sm text-[#13a4ec]">translate</span>
                <p className="text-white text-xs font-medium">Multi-language</p>
              </div>
              <div className="flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-[#233c48] px-4 border border-[#305263]">
                <span className="material-symbols-outlined text-sm text-[#13a4ec]">corporate_fare</span>
                <p className="text-white text-xs font-medium">Enterprise Scale</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <button className="flex min-w-[200px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 bg-[#13a4ec] text-white text-base font-bold tracking-[0.015em] hover:bg-[#13a4ec]/80 transition-all">
              <span>Contact Sales</span>
            </button>
            <button className="flex min-w-[200px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 bg-[#233c48] text-white text-base font-bold tracking-[0.015em] hover:bg-[#2d4d5c] transition-all">
              <span>View Pricing</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation - Exact match to Figma */}
          <aside className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24 flex flex-col gap-1 p-4 border border-[#233c48] rounded-xl bg-[#111c22]">
              <p className="text-[#92b7c9] text-xs font-bold uppercase tracking-widest px-3 py-2 mb-2">Jump to section</p>
              <button
                className={`px-3 py-2 rounded-lg font-medium hover:bg-[#233c48] transition-colors flex items-center gap-2 text-left ${
                  activeTab === 'overview' ? 'text-white bg-[#233c48]' : 'text-[#92b7c9] hover:text-white'
                }`}
                onClick={() => setActiveTab('overview')}
              >
                <span className="material-symbols-outlined text-sm">map</span> Overview & Mapping
              </button>
              <button
                className={`px-3 py-2 rounded-lg font-medium hover:bg-[#233c48] transition-colors flex items-center gap-2 text-left ${
                  activeTab === 'agents' ? 'text-white bg-[#233c48]' : 'text-[#92b7c9] hover:text-white'
                }`}
                onClick={() => setActiveTab('agents')}
              >
                <span className="material-symbols-outlined text-sm">smart_toy</span> AI Agent Inventory
              </button>
              <button
                className={`px-3 py-2 rounded-lg font-medium hover:bg-[#233c48] transition-colors flex items-center gap-2 text-left ${
                  activeTab === 'workflow' ? 'text-white bg-[#233c48]' : 'text-[#92b7c9] hover:text-white'
                }`}
                onClick={() => setActiveTab('workflow')}
              >
                <span className="material-symbols-outlined text-sm">account_tree</span> Example Workflow
              </button>
              <button
                className={`px-3 py-2 rounded-lg font-medium hover:bg-[#233c48] transition-colors flex items-center gap-2 text-left ${
                  activeTab === 'reviews' ? 'text-white bg-[#233c48]' : 'text-[#92b7c9] hover:text-white'
                }`}
                onClick={() => setActiveTab('reviews')}
              >
                <span className="material-symbols-outlined text-sm">star</span> Reviews
              </button>
            </div>
          </aside>

          {/* Main Content Sections */}
          <div className="lg:col-span-3 flex flex-col gap-12">
            {activeTab === 'overview' && (
              <section id="problem-mapping">
                <h2 className="text-white text-2xl font-bold leading-tight tracking-[-0.015em] pb-6 border-b border-[#233c48] mb-6 flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#13a4ec]">analytics</span>
                  Problem to AI Solution Mapping
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Row Header */}
                  <div className="hidden md:block text-[#92b7c9] text-xs font-bold uppercase tracking-widest px-4">Current Pain Point</div>
                  <div className="hidden md:block text-[#92b7c9] text-xs font-bold uppercase tracking-widest px-4">AI-Driven Solution</div>
                  
                  {service.problemMapping.map((mapping, index) => (
                    <div key={index} className="contents">
                      <div className="bg-[#182a33]/50 p-6 rounded-xl border-l-4 border-red-500/50">
                        <p className="text-white font-bold mb-2">{mapping.problem}</p>
                        <p className="text-[#92b7c9] text-sm">{mapping.problemDesc}</p>
                      </div>
                      <div className="bg-[#13a4ec]/5 p-6 rounded-xl border-l-4 border-[#13a4ec]">
                        <p className="text-[#13a4ec] font-bold mb-2">{mapping.solution}</p>
                        <p className="text-[#92b7c9] text-sm">{mapping.solutionDesc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'agents' && (
              <section id="agents">
                <h2 className="text-white text-2xl font-bold leading-tight tracking-[-0.015em] pb-6 border-b border-[#233c48] mb-6 flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#13a4ec]">robot_2</span>
                  AI Agent Inventory
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {service.agents.map((agent, index) => (
                    <div key={index} className="flex flex-col gap-4 p-5 rounded-xl border border-[#233c48] bg-[#182a33] hover:border-[#13a4ec]/50 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-[#13a4ec]/20 flex items-center justify-center text-[#13a4ec]">
                        <span className="material-symbols-outlined">{agent.icon}</span>
                      </div>
                      <h3 className="text-white font-bold text-lg">{agent.name}</h3>
                      <p className="text-[#92b7c9] text-sm leading-relaxed">{agent.description}</p>
                      <div className="mt-auto pt-4 flex items-center gap-2 text-[#13a4ec] text-xs font-bold uppercase tracking-wider">
                        <span>Status: {agent.status}</span>
                        <div className={`w-2 h-2 rounded-full ${agent.status === 'Active' ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'workflow' && (
              <section className="mb-12" id="workflow">
                <h2 className="text-white text-2xl font-bold leading-tight tracking-[-0.015em] pb-6 border-b border-[#233c48] mb-6 flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#13a4ec]">schema</span>
                  Example Workflow: "Smart Reservation & Arrival"
                </h2>
                <div 
                  className="w-full min-h-[400px] rounded-xl border border-[#233c48] flex flex-col items-center justify-center p-8 bg-[#111c22]" 
                  style={{
                    backgroundImage: 'radial-gradient(#233c48 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                >
                  {/* Diagram Elements */}
                  <div className="flex flex-col md:flex-row items-center gap-8 w-full max-w-4xl">
                    {/* Box 1 */}
                    <div className="flex-1 w-full bg-[#182a33] border border-[#233c48] p-6 rounded-lg text-center flex flex-col items-center gap-2">
                      <span className="material-symbols-outlined text-3xl text-[#13a4ec]">phone_iphone</span>
                      <p className="font-bold">Customer Input</p>
                      <p className="text-[#92b7c9] text-xs">Phone Call or Web Form</p>
                    </div>
                    <span className="material-symbols-outlined text-[#233c48] rotate-90 md:rotate-0 text-4xl">arrow_forward</span>
                    {/* Box 2 (AI Layer) */}
                    <div className="flex-1 w-full bg-[#13a4ec]/10 border-2 border-[#13a4ec] border-dashed p-6 rounded-lg text-center flex flex-col items-center gap-2 relative">
                      <div className="absolute -top-3 -right-3 bg-[#13a4ec] text-white text-[10px] px-2 py-1 rounded font-bold uppercase">AI Engine</div>
                      <span className="material-symbols-outlined text-3xl text-[#13a4ec]">neurology</span>
                      <p className="font-bold">Natural Language Processor</p>
                      <p className="text-[#92b7c9] text-xs">Intent Extraction & Table Check</p>
                    </div>
                    <span className="material-symbols-outlined text-[#233c48] rotate-90 md:rotate-0 text-4xl">arrow_forward</span>
                    {/* Box 3 */}
                    <div className="flex-1 w-full bg-[#182a33] border border-[#233c48] p-6 rounded-lg text-center flex flex-col items-center gap-2">
                      <span className="material-symbols-outlined text-3xl text-[#13a4ec]">kitchen</span>
                      <p className="font-bold">Operational Sync</p>
                      <p className="text-[#92b7c9] text-xs">Hostess App & Kitchen Notification</p>
                    </div>
                  </div>
                  <div className="mt-12 text-[#92b7c9] text-sm italic flex items-center gap-2">
                    <span className="material-symbols-outlined text-base">info</span>
                    Data flows through secure enterprise tunnels with &lt;100ms latency.
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'reviews' && (
              <section>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-white text-2xl font-bold leading-tight tracking-[-0.015em] flex items-center gap-3">
                    <span className="material-symbols-outlined text-[#13a4ec]">star</span>
                    Customer Reviews
                  </h2>
                  <div className="flex items-center gap-2">
                    <div className="flex">{renderStars(5)}</div>
                    <span className="text-sm text-[#92b7c9]">4.7 out of 5</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border border-[#233c48] rounded-lg p-4 bg-[#182a33]">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#233c48] flex items-center justify-center">
                          <span className="text-sm font-bold">{review.avatar}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">{review.user}</span>
                            <div className="flex">{renderStars(review.rating)}</div>
                            <span className="text-sm text-[#92b7c9]">{review.date}</span>
                          </div>
                          <p className="text-[#92b7c9]">{review.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Footer / CTA */}
        <div className="mt-8 border-t border-[#233c48] pt-12 pb-24 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to automate your restaurant group?</h2>
          <p className="text-[#92b7c9] mb-8 max-w-xl mx-auto">Get a personalized demo of the AI-Driven Automation platform tailored to your specific operational scale.</p>
          <div className="flex justify-center gap-4">
            <button className="flex min-w-[200px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 bg-[#13a4ec] text-white text-base font-bold tracking-[0.015em] hover:bg-[#13a4ec]/80 transition-all">
              <span>Book Consultation</span>
            </button>
            <button 
              onClick={() => downloadPDFGuide('Restaurant Automation')}
              className="flex min-w-[200px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 border border-[#233c48] bg-transparent text-white text-base font-bold tracking-[0.015em] hover:bg-[#233c48] transition-all"
            >
              <span>Download PDF Guide</span>
            </button>
          </div>
        </div>
      </main>

      {/* Bottom Footer Area */}
      <footer className="bg-[#0b1419] border-t border-[#233c48] px-10 xl:px-40 py-12 text-[#92b7c9] text-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-white">
              <span className="material-symbols-outlined text-[#13a4ec]">deployed_code</span>
              <span className="font-bold">OrbitMind</span>
            </div>
            <p>© 2024 OrbitMind Inc. All rights reserved.</p>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-white font-bold uppercase tracking-widest text-xs">Solutions</p>
            <Link to="/restaurant-ai" className="hover:text-white transition-colors">Restaurant AI</Link>
            <Link to="/logistics-hub" className="hover:text-white transition-colors">Logistics Hub</Link>
            <Link to="/retail-os" className="hover:text-white transition-colors">Retail OS</Link>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-white font-bold uppercase tracking-widest text-xs">Company</p>
            <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/security" className="hover:text-white transition-colors">Security</Link>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-white font-bold uppercase tracking-widest text-xs">Trust</p>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/gdpr" className="hover:text-white transition-colors">GDPR</Link>
            <Link to="/sla" className="hover:text-white transition-colors">SLA</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDetail;